//  --------------------------------------------------------------------------
//
//  I M P O R T S
//
//  --------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import '../../App.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

//  images
//  --------------------------------------------

//  classes
//  --------------------------------------------

import { useTranslation } from 'react-i18next';
import Scrollbox from '../../tools/scrollbox';
import ComputeSession from '../compute-tiles/compute-session';

//  types
//  --------------------------------------------

import { ComputeServiceType, Site } from '../../utils/types';

//  constants
//  --------------------------------------------

//  functions.
//  --------------------------------------------

import { brw_keyDownHandler } from '../../utils/item-browser';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { setSelectedSessionTypeDiv } from './panels-slice';

//  ------------------------------------------------------------
//
//  compute-services panel component.
//
//  ------------------------------------------------------------

export default function ComputeServicesPanel(props: {
  sites: Site[];
  flex?: string;
  stagingCatalog?: string;
}) {
  //
  // translation function
  const { t } = useTranslation();

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // local state variables.
  const sSelectedSessionTypeDiv: { divID: number } = useAppSelector(
    (state) => state.panels.selectedSessionTypeDiv,
  );

  // global-state variables.
  const sG_site: Site | undefined = useAppSelector((state) => state.global.site);

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  const _newSessionTiles: ComputeServiceType[] = [
    {
      site: sG_site ?? { site_uuid: '' },
      resource_type: 'canfar',
      session_type: 'notebook',
      compute_service_id: '',
      service_name: 'JupyterHUB',
      service_url: '',
      active_status: true,
      top_banner: '',
      is_embedded: true,
      compute_status: 'available',
    },
    {
      site: sG_site ?? { site_uuid: '' },
      resource_type: 'canfar',
      session_type: 'carta',
      compute_service_id: '',
      service_name: 'CARTA',
      service_url: '',
      active_status: true,
      top_banner: '',
      is_embedded: true,
      compute_status: 'available',
    },
    {
      site: sG_site ?? { site_uuid: '' },
      resource_type: 'canfar',
      session_type: 'desktop',
      compute_service_id: '',
      service_name: 'Virtual Desktop',
      service_url: '',
      active_status: true,
      top_banner: '',
      is_embedded: true,
      compute_status: 'available',
    },
    {
      site: sG_site ?? { site_uuid: '' },
      resource_type: 'canfar',
      session_type: 'headless',
      compute_service_id: '',
      service_name: 'Headless',
      service_url: '',
      active_status: true,
      top_banner: '',
      is_embedded: true,
      compute_status: 'available',
    },
  ];

  const _selectedSessionType: string =
    sSelectedSessionTypeDiv.divID === 2
      ? 'desktop'
      : sSelectedSessionTypeDiv.divID === 1
        ? 'visualise'
        : 'notebook';

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Set the focus to a specific item in the file browser.
  //
  //	------------------------------------------------------------

  const focusDataItem = () => {
    //
    let id: string = 'compute-panel-notebook';
    if (sSelectedSessionTypeDiv.divID === 1) {
      id = 'compute-panel-visualise';
    }
    if (sSelectedSessionTypeDiv.divID === 2) {
      id = 'compute-panel-desktop';
    }

    window.requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (target instanceof HTMLButtonElement) {
        target.focus();
      }
    });
    //
  }; // focusDataItem

  //	------------------------------------------------------------
  //
  //	Handle button clicks.
  //
  //	------------------------------------------------------------

  function onClickBtnHandler(event: React.MouseEvent<HTMLButtonElement>) {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // set the selected session type.
    if (button.id === 'compute-panel-notebook') {
      dispatch(
        setSelectedSessionTypeDiv({
          divID: 0,
        }),
      );
    }
    if (button.id === 'compute-panel-visualise') {
      dispatch(
        setSelectedSessionTypeDiv({
          divID: 1,
        }),
      );
    }
    if (button.id === 'compute-panel-desktop') {
      dispatch(
        setSelectedSessionTypeDiv({
          divID: 2,
        }),
      );
    }
    //
  } // onClickBtnHandler

  //	------------------------------------------------------------
  //
  //	Handler for when a key is pressed on an item.
  //
  //	------------------------------------------------------------

  function onKeyDownHandler(event: React.KeyboardEvent<HTMLElement>) {
    //
    // allow the item browser handler to handle this key down event.
    // this handler moves the focus from one item to another..
    brw_keyDownHandler({
      event: event,
      dataItems: 3,
      itemsPerRow: 1,
      selectedDivs: [],
      focusElement: sSelectedSessionTypeDiv.divID,
      setFocusElement: setSelectedSessionTypeDiv,
    });
    //
  } // onKeyDownHandler

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  Place the focus on the new selected project when the
  //  selected project is changed, or the focused div is changed.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    focusDataItem();
    //
  }, [sSelectedSessionTypeDiv]);

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <>
      <div
        className="text-colour"
        style={{
          display: 'flex',
          flexDirection: 'row',
          flex: '0 0',
          margin: '0px 10px 5px 10px',
        }}
      >
        Click on a tile to access these data in an interactive session:
      </div>
      <Scrollbox direction="horizontal" flex="0 0" width="100%" flexDirection="column">
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'start',
            verticalAlign: 'top',
          }}
        >
          {_newSessionTiles.map((item: ComputeServiceType) => (
            <ComputeSession session={item} stagingCatalog={props.stagingCatalog} />
          ))}
          <div style={{ flex: '0 0 10px' }} />
        </div>
      </Scrollbox>
    </>
  );
  //
} // ComputeServicesPanel
